import { useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { catalogMetadata, ingestedLabel, type CatalogRow } from "@/lib/geminiCatalogFilters";
import { VOICE_QUALITIES, voiceQualities, descriptionQualities } from "@/lib/voiceQualities";
export function GeminiVoiceProperties({ voice, onClose, onSaved }: {
  voice: CatalogRow & { test_phrase: string | null };
  onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(voice.display_name);
  const [narrator, setNarrator] = useState(voice.is_narrator === true);
  const initialQualities = voiceQualities(voice.provider_metadata);
  const [qualities, setQualities] = useState(initialQualities);
  const [restoreQualities, setRestoreQualities] = useState(false);
  const qualitiesDirty = restoreQualities || JSON.stringify(qualities) !== JSON.stringify(initialQualities);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dialogue, setDialogue] = useState(voice.test_phrase ?? "");
  const [generating, setGenerating] = useState(false);
  const inFlight = useRef(false);
  const phraseDirty = dialogue !== (voice.test_phrase ?? "");
  const propertiesDirty = name !== voice.display_name || narrator !== (voice.is_narrator === true) || qualitiesDirty;
  const locked = busy || phraseDirty;
  async function generatePreview() {
    if (inFlight.current || propertiesDirty) return;
    inFlight.current = true; setBusy(true); setGenerating(true); setError("");
    try {
      const expected = { sample_audio_url: voice.sample_audio_url ?? null, test_phrase: voice.test_phrase, updated_at: voice.updated_at };
      const fingerprint = JSON.stringify({ text: dialogue.trim(), expected });
      const storageKey = `tableread.admin.preview-request:${voice.id}`;
      // Preserve the same request across response loss/reopening this dialog.
      // If storage is blocked, stop before any paid call rather than lose its receipt.
      const previous = JSON.parse(sessionStorage.getItem(storageKey) ?? "null");
      const requestId = previous?.fingerprint === fingerprint ? previous.id : crypto.randomUUID();
      sessionStorage.setItem(storageKey, JSON.stringify({ id: requestId, fingerprint }));
      const result = await supabase.functions.invoke("gemini-catalog", { body: {
        action: "regenerate-sample", voice_id: voice.id, text: dialogue.trim(), request_id: requestId, expected,
      } });
      let detail = result.data;
      if (result.error?.context instanceof Response) {
        try { detail = await result.error.context.json(); } catch { /* retain the safe fallback */ }
      }
      if (result.error || detail?.error) throw new Error(detail?.error || "Preview request interrupted. Retry with the same dialogue to check the saved result without starting another paid request.");
      if (!detail?.saved) {
        sessionStorage.removeItem(storageKey);
        throw new Error(`The preview was ${Number(detail?.duration).toFixed(1)} seconds. Edit the dialogue to target 7–10 seconds, then generate again. The previous preview is unchanged.`);
      }
      sessionStorage.removeItem(storageKey);
      onSaved(); onClose();
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to generate the preview. The previous preview is unchanged."); }
    finally { inFlight.current = false; setBusy(false); setGenerating(false); }
  }
  async function save() {
    setBusy(true); setError("");
    try {
      const result = await supabase.functions.invoke("gemini-catalog", { body: {
        action: "update", voice_id: voice.id, display_name: name, is_active: voice.is_active,
        expected: { display_name: voice.display_name, is_active: voice.is_active, narrator: voice.is_narrator === true, metadata: voice.provider_metadata },
        trait_updates: { ...(qualitiesDirty ? { qualities: restoreQualities ? null : qualities } : {}), ...(narrator !== (voice.is_narrator === true) ? { narrator } : {}) },
      } });
      let detail = result.data;
      if (result.error?.context instanceof Response) { try { detail = await result.error.context.json(); } catch { /* safe fallback below */ } }
      if (result.error || detail?.error) throw new Error(detail?.error || "Unable to save Gemini voice. Please retry.");
      onSaved(); onClose();
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to save voice."); }
    finally { setBusy(false); }
  }
  return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto">
      <DialogHeader><DialogTitle>Gemini voice properties</DialogTitle>
        <DialogDescription>Correct the labels used for filtering. These edits do not change the generated voice, saved audio, or screenplay casting.</DialogDescription></DialogHeader>
      <p className="text-xs text-muted-foreground">Gemini voice: {String(voice.provider_metadata?.voice ?? "—")}</p>
      <p className="text-xs text-muted-foreground">Ingested: <time dateTime={voice.created_at}>{ingestedLabel(voice.created_at)}</time></p>
      <Label htmlFor="gemini-voice-name">Voice actor name</Label>
      <Input id="gemini-voice-name" maxLength={100} value={name} onChange={e => setName(e.target.value)} disabled={locked} />
      <div className="space-y-1">
        <div className="flex items-center gap-2"><Checkbox id="gemini-voice-narrator" checked={narrator}
          onCheckedChange={v => setNarrator(v === true)} disabled={locked} /><Label htmlFor="gemini-voice-narrator">Preferred narrator</Label></div>
        <p className="text-xs text-muted-foreground">When active, this voice can be randomly selected for Scene Narrator by Auto-Assign.</p>
      </div>
      {typeof catalogMetadata(voice).description === "string" && <p className="text-sm text-muted-foreground">{String(catalogMetadata(voice).description)}</p>}
      <fieldset className="min-w-0 space-y-2" disabled={locked} aria-describedby="voice-qualities-help">
        <legend className="text-sm font-medium">Voice qualities</legend>
        <div className="max-h-48 overflow-y-auto overscroll-contain rounded-md border border-input bg-background p-1">
          {VOICE_QUALITIES.map(quality => <label key={quality} className="flex min-h-8 cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
            <input type="checkbox" className="accent-primary" checked={qualities.includes(quality)} onChange={event => {
              const checked = event.target.checked;
              setQualities(previous => VOICE_QUALITIES.filter(item => item === quality ? checked : previous.includes(item)));
              setRestoreQualities(false);
            }} />
            <span>{quality}</span>
          </label>)}
        </div>
        <p id="voice-qualities-help" className="text-xs text-muted-foreground">Initially selected from the Gemini description. Save voice to keep your selections.</p>
        <Button type="button" variant="outline" size="sm" disabled={locked} onClick={() => { setQualities(descriptionQualities(voice.provider_metadata)); setRestoreQualities(true); }}>Use Gemini description</Button>
      </fieldset>
      <p className="text-sm text-muted-foreground">Screenplay emotional highlights become Gemini delivery instructions.
        They are expressive directions, not exact pitch or volume controls.</p>
      <div className="space-y-2">
        <Label htmlFor="voice-preview-dialogue">Voice preview dialogue</Label>
        <Textarea id="voice-preview-dialogue" value={dialogue} onChange={event => setDialogue(event.target.value)} maxLength={400} rows={4} disabled={busy || propertiesDirty} aria-describedby="preview-generation-help" />
        <p id="preview-generation-help" className="text-xs text-muted-foreground">Use original screenplay-style dialogue, 30–400 characters. Generate a new 7–10 second preview to save this text with its audio. The existing preview stays available unless a valid replacement is saved. Actor settings and screenplay audio are unchanged.</p>
        {propertiesDirty && <p className="text-xs text-muted-foreground">Save voice properties first, then reopen this voice to edit its preview.</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => void generatePreview()} disabled={busy || propertiesDirty || dialogue.trim().length < 30}>{generating ? "Generating preview…" : "Generate new voice preview"}</Button>
          {phraseDirty && <Button type="button" variant="ghost" disabled={busy} onClick={() => setDialogue(voice.test_phrase ?? "")}>Restore saved dialogue</Button>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">New Gemini audio counts toward the configured site-wide daily generation allowance. Cached playback does not make another Gemini request.</p>
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
      <DialogFooter><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => void save()} disabled={busy || phraseDirty || !name.trim()}>{busy && !generating ? "Saving…" : "Save voice"}</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}
